import { defineStore, acceptHMRUpdate } from 'pinia';
import JsonPatchSrv from '@/js/services/JsonPatchSrv';
import usePlanningStore from './planning/planning';

const maxUndo = 30;

/** Action Model */
function UndoRedoAction(execute, rollback) {
  this.execute = execute;
  this.rollback = rollback;
}

const store = defineStore('undoredo', {
  state: () => ({
    undos: [],
    redos: [],
    locked: false, // for transactions
  }),

  getters: {
    isUndoable() {
      return this.undos.length !== 0;
    },
    isRedoable() {
      return this.redos.length !== 0;
    },
  },

  actions: {
    addUndo(newel) {
      this.undos.push(newel);
      if (this.undos.length > maxUndo) this.undos.shift();
    },
    addRedo(newel) {
      this.redos.push(newel);
    },
    emptyUndo() {
      this.undos.splice(0, this.undos.length);
    },
    emptyRedo() {
      this.redos.splice(0, this.redos.length);
    },

    undo() {
      const action = this.undos.pop();
      if (! action) return;
      action.rollback();
      this.redos.push(action);
      const planningStore = usePlanningStore();
      setTimeout(() => planningStore.save());
    },

    redo() {
      const action = this.redos.pop();
      if (! action) return;
      action.execute();
      this.undos.push(action);
      const planningStore = usePlanningStore();
      setTimeout(() => planningStore.save());
    },

    transaction(cb) {
      /* group undos redos in a single batch */
      const { planning } = usePlanningStore();
      this.locked = true;
      const beforeVersion = {
        content: {
          config: planning.config.getAll(),
          timeline: planning.timeline.getAll(),
          lanes: planning.lanes.map(lane => lane.getData()),
          elements: planning.elements.map(el => ({ id: el.id, ...el.getData() })),
          process: { config: planning.process.config.getAll(), steps: planning.process.steps.map(step => step.getAll()) },
        },
      };
      cb();
      const currentVersion = {
        content: {
          config: planning.config.getAll(),
          timeline: planning.timeline.getAll(),
          lanes: planning.lanes.map(lane => lane.getData()),
          elements: planning.elements.map(el => ({ id: el.id, ...el.getData() })),
          process: { config: planning.process.config.getAll(), steps: planning.process.steps.map(step => step.getAll()) },
        },
      };
      this.locked = false;

      const newStatePatches = JsonPatchSrv.getProjectDiff(beforeVersion, currentVersion);
      if (! newStatePatches.length) return;
      const oldStatePatches = JsonPatchSrv.getProjectDiff(currentVersion, beforeVersion);
      this.add({
        action: () => {
          const modifications = planning.applyContentPatch(newStatePatches, false);
          planning.elements.forEach((el) => {
            if (modifications.elements[el.id]) {
              el.update();
              setTimeout(() => el.updateHeight());
            }
          });
        },
        rollback: () => {
          const modifications = planning.applyContentPatch(oldStatePatches, false);
          planning.elements.forEach((el) => {
            if (modifications.elements[el.id]) {
              el.update();
              setTimeout(() => el.updateHeight());
            }
          });
        },
      });
    },

    /** Add a new Rollabackable action */
    add({ action, rollback }) {
      if (this.locked) return;
      this.addUndo(new UndoRedoAction(action, rollback));
      this.emptyRedo();
    },

    reset() {
      this.emptyUndo();
      this.emptyRedo();
    },
  },
});

if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(store, import.meta.hot));
export default store;
